import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

type ContactSalesVariant = 'public' | 'agency';

interface ContactSalesProps {
  variant?: ContactSalesVariant;
}

const PLAN_HIGHLIGHTS: Record<
  string,
  {
    headline: string;
    subhead: string;
    bullets: string[];
    metrics: Array<{ label: string; value: string }>;
  }
> = {
  Enterprise: {
    headline: 'Enterprise workspace for modern agencies',
    subhead: 'Unlimited seats, properties, and customised workflows',
    bullets: [
      'Dedicated success manager & migration support',
      'Custom data residency & compliance options',
      'End-to-end workflow automation across sales, leasing, and PM',
    ],
    metrics: [
      { label: 'Users', value: 'Unlimited' },
      { label: 'Active properties', value: 'Unlimited' },
      { label: 'Storage', value: 'Customised' },
    ],
  },
  Premium: {
    headline: 'Premium automation & collaboration',
    subhead: 'Best for high-volume offices scaling workflows',
    bullets: [
      'Advanced reporting suite',
      'Attendee management + smart follow-ups',
      'Domain.com.au & RealEstate.com.au sync',
    ],
    metrics: [
      { label: 'Users', value: 'Unlimited' },
      { label: 'Active properties', value: 'Unlimited' },
      { label: 'Storage', value: '20GB' },
    ],
  },
};

const ContactSales: React.FC<ContactSalesProps> = ({ variant = 'public' }) => {
  const { agency } = useAuth();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') || 'Enterprise';
  const navigate = useNavigate();
  const params = useParams();
  const agencySlugFromRoute = params.agencySlug;

  const [companyName, setCompanyName] = useState(agency?.name || '');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState(agency?.email || '');
  const [phone, setPhone] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [activeProperties, setActiveProperties] = useState('');
  const [timeline, setTimeline] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const planInfo = useMemo(() => {
    return PLAN_HIGHLIGHTS[plan] || PLAN_HIGHLIGHTS['Enterprise'];
  }, [plan]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);

    try {
      const composedMessage = `
Requested Plan: ${plan}
Company: ${companyName}
Contact: ${contactName}
Phone: ${phone}
Team Size: ${teamSize || '–'}
Active Properties: ${activeProperties || '–'}
Timeline: ${timeline || '–'}
Agency Slug: ${agencySlugFromRoute || agency?.slug || 'n/a'}

${message || 'No additional context supplied.'}
      `.trim();

      await api.post('/customers/messages', {
        name: contactName || companyName || 'Prospective Customer',
        email,
        message: composedMessage,
      });

      setSuccess('Thanks for your interest! Our sales team will contact you shortly.');
      setCompanyName('');
      setContactName('');
      setPhone('');
      setTeamSize('');
      setActiveProperties('');
      setTimeline('');
      setMessage('');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (variant === 'agency') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <Box sx={{ backgroundColor: variant === 'public' ? '#f4f7fb' : 'transparent', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Stack spacing={3} height="100%">
              <Box>
                <Chip label={`${plan} plan`} color="primary" variant="outlined" />
                <Typography variant="h4" fontWeight={700} mt={2}>
                  Contact Sales
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1.5}>
                  Tell us about your agency and we’ll create a tailored pricing package, onboarding timeline, and success plan.
                </Typography>
              </Box>

              <Paper elevation={2} sx={{ p: 3, borderRadius: 3, flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {planInfo.headline}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {planInfo.subhead}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  {planInfo.bullets.map((bullet, idx) => (
                    <Stack direction="row" spacing={1} alignItems="flex-start" key={idx}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{bullet}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {planInfo.metrics.map((metric) => (
                    <Grid item xs={6} key={metric.label}>
                      <Typography variant="caption" color="text.secondary">
                        {metric.label}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {metric.value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Typography variant="h6" gutterBottom>
                  Share your details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Our team typically responds within one business day.
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Company name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact name"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Work email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Team size"
                        placeholder="e.g. 35 agents"
                        value={teamSize}
                        onChange={(e) => setTeamSize(e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Active properties"
                        placeholder="e.g. 120 managed"
                        value={activeProperties}
                        onChange={(e) => setActiveProperties(e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Ideal go-live timeline"
                        placeholder="e.g. Within 4 weeks"
                        value={timeline}
                        onChange={(e) => setTimeline(e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Anything else we should know?"
                        multiline
                        minRows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        helperText="Share goals, current tools, integration needs, or compliance requirements."
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
                    <Button variant="text" onClick={handleBack} disabled={submitting}>
                      Back
                    </Button>
                    <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                      {submitting ? <CircularProgress size={24} /> : 'Submit request'}
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactSales;

